import { FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import { Dispatch, SetStateAction } from 'react';
import { BulkImportType } from '../../../../../types';
import { BulkOpsFormValues } from './create-bulk-op-dialog';
import SharedFilePickerOld from '../../../../shared/shared-file-picker-old';
import SharedFilePicker from '../../../../shared/shared-file-picker';
import { useGenerateTemplate } from './use-generate-template';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { isFeatureEnabled } from '../../../../utils/feature-flags';

import Button from '@mui/material/Button';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grow from '@mui/material/Grow';
import Description from '@mui/icons-material/Description';

interface StepSpreadsheetTemplateOldProps {
  setValue: UseFormSetValue<BulkOpsFormValues>;
  watch: UseFormWatch<BulkOpsFormValues>;
  spreadSheetName: string;
  setSpreadSheetName: Dispatch<SetStateAction<string>>;
  selectedBulkImportType: BulkImportType | null;
  selectedOption: 'template' | 'existing' | null;
  setSelectedOption: Dispatch<SetStateAction<'template' | 'existing' | null>>;
  errors: FieldErrors<BulkOpsFormValues>;
}

export default function StepSpreadsheetTemplateOld({
  setValue,
  watch,
  spreadSheetName,
  setSpreadSheetName,
  selectedBulkImportType,
  selectedOption,
  setSelectedOption,
  errors,
}: StepSpreadsheetTemplateOldProps) {
  const spreadsheetId = watch('spreadsheetId') || '';
  const { features } = useUserSettings();
  // Clear spreadsheet selection when switching radio options
  const handleRadioChange = (option: 'template' | 'existing') => {
    setSelectedOption(option);
    setValue('spreadsheetId', '');
    setSpreadSheetName('');
  };
  const { handleUseTemplate, isGeneratingTemplate } = useGenerateTemplate(
    setValue,
    setSpreadSheetName,
    selectedBulkImportType
  );

  return (
    <Stack gap={2} width={'100%'}>
      <Typography sx={{ mb: 3, color: 'text.secondary' }}>
        To ensure your bulk operation runs smoothly, please use a spreadsheet
        that includes all required column headers. Tip: Using our template is
        the easiest way to ensure your data is formatted correctly.
      </Typography>
      {/* Template Option */}
      <Stack direction="row" alignItems="flex-start" gap={1}>
        <Radio
          checked={selectedOption === 'template'}
          onChange={() => handleRadioChange('template')}
          value="template"
          name="spreadsheet-option"
        />
        <Stack>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="h6">Start with our template</Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            We'll provide a pre-formatted Google Sheet to help you get started.
            <br />
            This spreadsheet will save to your Google My Drive and will be
            automatically
            <br />
            selected as your spreadsheet for this bulk operation.
          </Typography>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          sx={{ marginLeft: '6px' }}
        >
          <Button
            variant="contained"
            onClick={handleUseTemplate}
            disabled={
              isGeneratingTemplate ||
              selectedOption !== 'template' ||
              !selectedBulkImportType
            }
            color="primary"
            loading={isGeneratingTemplate}
            startIcon={<Description />}
          >
            Use Template
          </Button>
          <Box sx={{ maxWidth: '350px' }}>
            {spreadSheetName && selectedOption === 'template' && (
              <Tooltip title={spreadSheetName} arrow>
                <Chip
                  avatar={<Description />}
                  label={spreadSheetName}
                  onDelete={(e) => {
                    e.preventDefault();
                    setValue('spreadsheetId', '');
                    setSpreadSheetName('');
                  }}
                  size="small"
                  clickable={spreadsheetId !== ''}
                  component={spreadsheetId !== '' ? 'a' : 'div'}
                  href={
                    spreadsheetId !== ''
                      ? `https://docs.google.com/spreadsheets/d/${spreadsheetId}`
                      : undefined
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                />
              </Tooltip>
            )}
          </Box>
        </Stack>
      </Stack>

      {/* Existing Spreadsheet Option */}
      <Stack direction="row" alignItems="flex-start" gap={1}>
        <Radio
          checked={selectedOption === 'existing'}
          onChange={() => handleRadioChange('existing')}
          value="existing"
          name="spreadsheet-option"
        />
        <Stack>
          <Typography variant="h6">I already have a spreadsheet</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Choose an existing Google Sheet that is already formatted correctly.
          </Typography>
        </Stack>
        <Box sx={{ marginLeft: '60px' }}>
          {isFeatureEnabled('drivePickerUIReact', features) ? (
            <SharedFilePicker
              pickerType="spreadsheet"
              selectedFileId={spreadsheetId || ''}
              selectedFileName={spreadSheetName}
              onFileSelected={({ id, name }) => {
                setValue('spreadsheetId', id);
                setSpreadSheetName(name);
              }}
              onFileRemoved={() => {
                setValue('spreadsheetId', '');
                setSpreadSheetName('');
              }}
              disabled={selectedOption !== 'existing'}
              maxWidth="500px"
              includeSharedDrives
            />
          ) : (
            <SharedFilePickerOld
              pickerType="spreadsheet"
              selectedFileId={spreadsheetId || ''}
              selectedFileName={spreadSheetName}
              onFileSelected={({ id, name }) => {
                setValue('spreadsheetId', id);
                setSpreadSheetName(name);
              }}
              onFileRemoved={() => {
                setValue('spreadsheetId', '');
                setSpreadSheetName('');
              }}
              disabled={selectedOption !== 'existing'}
              maxWidth="500px"
              includeSharedDrives
            />
          )}
        </Box>
      </Stack>
      <Stack sx={{ mt: 2 }}>
        {errors.spreadsheetId ? (
          <Box>
            <Grow in={true}>
              <Alert severity="error" sx={{ width: 'fit-content' }}>
                {errors.spreadsheetId.message}
              </Alert>
            </Grow>
          </Box>
        ) : null}
      </Stack>
    </Stack>
  );
}
